import { Controller } from "@hotwired/stimulus";
import * as lo from "lodash";
import { verifyInterface } from "../utils";

export default class extends Controller {
  /**
   * Virtual Keyboard Controller
   */

  static targets = ["form", "clearable"];
  connect() {
    verifyInterface(this, {
      requiredInterface: ["formTarget"],
      allValues: true,
    });

    const quickAction = document.getElementById("mobile-quick-action");
    if (quickAction) {
      quickAction.remove();
    }
    const submit = () => this.formTarget.requestSubmit();
    const maybeSubmit = lo.debounce(submit, 500);

    Keyboard.init((_valueOfInputOnSubmit) => {
      submit();
    });
    this.element.querySelectorAll("input").forEach((inputEl) => {
      inputEl.addEventListener("input", maybeSubmit);
    });
  }

  clear() {
    if (!this.hasClearableTarget) {
      throw new Error(
        "DEVELOPMENT ERROR - requested clear but did not register any data-keyboard-target='clearable'.",
      );
    }
    this.clearableTargets.forEach((inputEl) => {
      inputEl.value = "";
    });
    Keyboard.properties.value = "";
    this.formTarget.requestSubmit();
  }
}

const Keyboard = {
  multiMode: true,
  selectedElement: "",
  elements: {
    main: null,
    keysContainer: null,
    keys: [],
  },
  keyboard_type: "numeric",
  eventHandlers: {
    oninput: null,
  },

  properties: {
    value: "",
  },

  init(submitCallback) {
    // Create main elements
    Keyboard.elements.main = document.createElement("div");
    Keyboard.elements.keysContainer = document.createElement("div");

    Keyboard.eventHandlers.submit = submitCallback;
    // Setup main elements
    Keyboard.elements.main.classList.add("keyboard", "keyboard--hidden");
    Keyboard.elements.keysContainer.classList.add("keyboard__keys");

    if (Keyboard.multiMode === false) {
      // Run only full keyboard
      Keyboard._setupKeyboard("alfa");
    }

    Keyboard.elements.keys =
      Keyboard.elements.keysContainer.querySelectorAll(".keyboard__key");
    Keyboard.elements.main.appendChild(Keyboard.elements.keysContainer);
    document.querySelector("body").appendChild(Keyboard.elements.main);

    const createKeyboard = (event) => {
      if (event.target.matches('input[type="email"]')) {
        Keyboard._setupKeyboard("alfa");
        Keyboard.selectedElement = event.target;
        Keyboard.open(event.target.value, (currentValue) => {
          event.target.value = currentValue;
        });
      }

      if (event.target.matches('input[type="number"]')) {
        Keyboard._setupKeyboard("numeric");
        Keyboard.selectedElement = event.target;
        Keyboard.open(event.target.value, (currentValue) => {
          event.target.value = currentValue;
        });
      }

      if (event.target.matches('input[type="text"]')) {
        Keyboard._setupKeyboard("alfa");
        Keyboard.selectedElement = event.target;
        Keyboard.open(event.target.value, (currentValue) => {
          event.target.value = currentValue;
        });
      }

      if (event.target.matches('input[type="password"]')) {
        Keyboard._setupKeyboard("alfa");
        Keyboard.selectedElement = event.target;
        Keyboard.open(event.target.value, (currentValue) => {
          event.target.value = currentValue;
        });
      }

      if (event.target.matches('input[class="p-input--bonus"]')) {
        Keyboard._setupKeyboard("alfa");
        Keyboard.selectedElement = event.target;
        Keyboard.open(event.target.value, (currentValue) => {
          event.target.value = currentValue;
        });
      }
    };

    document.addEventListener("click", createKeyboard, true);
    document.addEventListener("focusin", createKeyboard, true);
  },

  _setupKeyboard(type) {
    Keyboard.elements.keysContainer.innerHTML = "";
    Keyboard.elements.keysContainer.appendChild(Keyboard._createKeys(type));
  },
  _createKeys(keyboard_type) {
    const fragment = document.createDocumentFragment();
    let keyLayout = [];
    if (keyboard_type === "numeric") {
      keyLayout = [
        ["7", "8", "9"],
        ["4", "5", "6"],
        ["1", "2", "3"],
        ["backspace", "submit"],
      ];
    } else {
      keyLayout = [
        ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "backspace"],
        ["q", "w", "e", "r", "t", "z", "u", "i", "o", "p", "ü"],
        ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä"],
        ["-", "_", "y", "x", "c", "v", "b", "n", "m", ",", "."],
        ["space"],
      ];
    }

    // Creates HTML for an icon
    const createIconHTML = (icon_name) => {
      return `<i class="bi bi-${icon_name}"></i>`;
    };

    keyLayout.forEach((row) => {
      row.forEach((key) => {
        const keyElement = document.createElement("button");

        // Add attributes/classes
        keyElement.setAttribute("type", "button");
        keyElement.classList.add("keyboard__key");

        switch (key) {
          case "backspace":
            keyElement.classList.add(
              "keyboard__key--wide",
              "text-wattro-yellow",
            );
            keyElement.innerHTML = createIconHTML("backspace-fill");

            keyElement.addEventListener("click", () => {
              this.properties.value = this.properties.value.substring(
                0,
                this.properties.value.length - 1,
              );
              this._triggerEvent("oninput");
              this._fireKeyEvent();
            });

            break;

          case "enter":
            keyElement.classList.add("keyboard__key--wide");
            keyElement.innerHTML = createIconHTML("arrow-return-left");

            keyElement.addEventListener("click", () => {
              this.properties.value += "\n";
              this._triggerEvent("oninput");
            });

            break;

          case "space":
            keyElement.classList.add("keyboard__key--extra-wide");
            keyElement.innerHTML = " ";

            keyElement.addEventListener("click", () => {
              this.properties.value += " ";
              this._triggerEvent("oninput");
            });

            break;

          case "submit":
            keyElement.classList.add("keyboard__key--wide");
            keyElement.innerHTML = createIconHTML("check-circle-fill");

            keyElement.addEventListener("click", () => {
              this._triggerEvent("submit");
            });

            break;

          default:
            keyElement.textContent = key.toLowerCase();
            keyElement.addEventListener("click", () => {
              this.properties.value += key;
              this._triggerEvent("oninput");

              // Propagate Keyboard event
              this._fireKeyEvent();
            });

            break;
        }

        fragment.appendChild(keyElement);
      });
      fragment.appendChild(document.createElement("br"));
    });

    return fragment;
  },

  _fireKeyEvent() {
    let evt = new KeyboardEvent("input", {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    // Create and dispatch keyboard simulated Event
    Keyboard.selectedElement.dispatchEvent(evt);
  },

  _triggerEvent(handlerName) {
    if (typeof this.eventHandlers[handlerName] == "function") {
      this.eventHandlers[handlerName](this.properties.value);
    } else {
      console.error("DEVELOPMENT ERROR - unregistered handler", handlerName);
    }
  },

  open(initialValue, oninput) {
    this.properties.value = initialValue || "";
    this.eventHandlers.oninput = oninput;
    this.elements.main.classList.remove("keyboard--hidden");
  },

  close() {
    this.properties.value = "";
    this.eventHandlers.oninput = null;
    this.elements.main.classList.add("keyboard--hidden");
  },
};
